import React, { Suspense, lazy } from 'react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { User } from '@/types';
import { Shield } from 'lucide-react';

// SaaS Admin 컴포넌트
import CustomerManagement from '@/components/users/customer/superadmin';
import UserManagement from '@/components/users/management/admin';
import UserManagementSuperadmin from '@/components/users/management/superadmin';
import ServiceManagement from '@/components/services/management/superadmin';
import ServiceCreditPlanPage from '@/components/services/management/superadmin/ServiceCreditPlanPage';
import LoginIntegrationPage from '@/components/services/management/superadmin/LoginIntegrationPage';
// 그래프 라이브러리(recharts)가 크다. 이 화면을 열 때만 불러온다 (#241)
const ActivityPage = lazy(() => import('@/components/services/management/superadmin/activity'));
import Settings from '@/components/settings/environment/superadmin';
import EmailTemplateManagement from '@/components/settings/environment/superadmin/EmailTemplateManagement';
import ActivityLogs from '@/components/settings/activity-logs/admin';
import ActivityLogsSuperadmin from '@/components/settings/activity-logs/superadmin';
import SystemLogs from '@/components/settings/activity-logs/superadmin/SystemLogs';
import AIMonitoring from '@/pages/superadmin/AIMonitoring';
import AIProviderSettings from '@/pages/superadmin/ai/AIProviderSettings';

// Customer Admin & User Dashboard 컴포넌트
import CustomerAdminDashboard from '@/components/services/management/admin';
import CustomerUserDashboard from '@/components/services/management/user';

type DashboardTab = 'services' | 'search' | 'users' | 'settings' | 'logs';

// 슈퍼관리자 모드 배지
const SuperAdminBadge: React.FC = () => (
  <div className="flex items-center gap-2 mb-4">
    <span className="superadmin-mode-badge">
      <Shield className="h-3 w-3" />
      슈퍼관리자 모드
    </span>
  </div>
);

// 슈퍼관리자 전용 서브메뉴 목록 (보라색 배경 적용)
export const SUPERADMIN_ONLY_SUBMENUS = [
  'customer-management',
  'user-management-all',
  'service-management',
  'service-credit-plans',
  'login-integrations',
  'user-activity',
  'environment-settings',
  'email-templates',
  'user-logs-all',
  'system-logs',
  'ai-monitoring',
  'ai-provider-settings',
];

interface DashboardPageContentProps {
  user: User;
  activeSubMenu: string;
}

export const DashboardPageContent: React.FC<DashboardPageContentProps> = ({ user, activeSubMenu }) => {
  if (user.role === 'saas_admin') {
    // 슈퍼관리자 전용 페이지인지 확인
    const isSuperAdminOnly = SUPERADMIN_ONLY_SUBMENUS.includes(activeSubMenu);

    // 컨텐츠 렌더링
    let content: React.ReactNode;
    switch (activeSubMenu) {
      case 'customer-management':
        content = <CustomerManagement />;
        break;
      case 'user-management':
        content = <UserManagement />;
        break;
      case 'user-management-all':
        content = <UserManagementSuperadmin />;
        break;
      case 'service-usage':
        content = <CustomerAdminDashboard />;
        break;
      case 'service-management':
        content = <ServiceManagement />;
        break;
      case 'service-credit-plans':
        content = <ServiceCreditPlanPage />;
        break;
      case 'login-integrations':
        content = <LoginIntegrationPage />;
        break;
      case 'user-activity':
        content = (
          <Suspense fallback={<LoadingSpinner message="이용 · 결제 현황을 불러오는 중입니다." />}>
            <ActivityPage />
          </Suspense>
        );
        break;
      case 'environment-settings':
        content = <Settings />;
        break;
      case 'email-templates':
        content = <EmailTemplateManagement />;
        break;
      case 'activity-logs':
        content = <ActivityLogs customerId={user.customer?.id || null} logType="user_logs" />;
        break;
      case 'user-logs-all':
        content = <ActivityLogsSuperadmin />;
        break;
      case 'system-logs':
        content = <SystemLogs />;
        break;
      case 'ai-monitoring':
        content = <AIMonitoring />;
        break;
      case 'ai-provider-settings':
        content = <AIProviderSettings />;
        break;
      case 'customer-settings':
        content = <CustomerAdminDashboard initialTab="settings" />;
        break;
      default:
        content = <UserManagement />;
        break;
    }

    // 슈퍼관리자 전용 페이지는 배지 표시
    return isSuperAdminOnly ? (
      <>
        <SuperAdminBadge />
        {content}
      </>
    ) : (
      content
    );
  }
  
  // Customer Admin용 이용자 관리
  if (user.role === 'admin' && activeSubMenu === 'user-management') {
    return <UserManagement />;
  }
  
  const menuToTabMap: Record<string, DashboardTab> = {
    'service-usage': 'services',
    'customer-settings': 'settings',
    'activity-logs': 'logs',
  };

  const tabFromMenu: DashboardTab = menuToTabMap[activeSubMenu] || 'services';

  // 서비스 이용 화면에서는 탭 전환을 컴포넌트 내부 URL(tab 파라미터)로만 관리
  if (activeSubMenu === 'service-usage') {
    if (user.role === 'admin') {
      return <CustomerAdminDashboard initialTab="services" />;
    } else {
      return <CustomerUserDashboard initialTab="services" />;
    }
  }
  
  // 설정/로그 등 다른 submenu 처리
  if (user.role === 'admin') {
    return <CustomerAdminDashboard initialTab={tabFromMenu} />;
  } else {
    // 일반 사용자의 경우 activity-logs는 ActivityLogs 컴포넌트를 직접 렌더링
    if (activeSubMenu === 'activity-logs') {
      return <ActivityLogs customerId={user.customer?.id || null} />;
    }
    // CustomerUserDashboard는 'settings' 탭을 지원하지 않으므로 필터링
    const userTab = tabFromMenu === 'settings' ? 'services' : tabFromMenu;
    return <CustomerUserDashboard initialTab={userTab as 'services' | 'search' | 'users' | 'logs'} />;
  }
};
